/**
 * AI Theme Player
 *
 * CSS-based slide renderer that displays PresentationContent with theme styling.
 * Uses CSS custom properties for instant theme switching without recompilation.
 */

import React, { useMemo, useState, useCallback } from 'react';
import { ScreenContent, ContentBlock, ImagePlaceholderBlock, TOCListBlock, StatBlock, ChartBlock } from '../../ai/schemas/ContentBlock';
import { aiService } from './services/AIService';
import { StyleTheme } from '../../ai/schemas/StyleTheme';
import {
  DecorationElement,
  GradientDef,
  getDecorationsForScreen,
  parseDecorationColor,
} from '../../ai/styles/themeDecorations';

/**
 * Theme player props
 */
interface AIThemePlayerProps {
  screen: ScreenContent;
  theme: StyleTheme;
  aspectRatio?: { width: number; height: number };
  scale?: number;
  onImageResolved?: (blockId: string, imageUrl: string) => void;
  onScreenNavigation?: (screenIndex: number) => void;
}

/**
 * Convert theme to CSS custom properties
 */
function themeToCSSVars(theme: StyleTheme): React.CSSProperties {
  return {
    '--theme-bg': theme.colors.background,
    '--theme-text': theme.colors.textPrimary,
    '--theme-text-secondary': theme.colors.textSecondary,
    '--theme-primary': theme.colors.primary,
    '--theme-secondary': theme.colors.secondary,
    '--theme-accent': theme.colors.accent[0] || theme.colors.primary,
    '--theme-title-font': theme.typography.titleFont,
    '--theme-body-font': theme.typography.bodyFont,
    '--theme-base-size': `${theme.typography.baseFontSize || 18}px`,
    '--theme-line-height': theme.typography.lineHeight || 1.5,
    '--theme-padding': `${theme.spacing.screenPadding}px`,
    '--theme-gap': `${theme.spacing.elementGap}px`,
  } as React.CSSProperties;
}


/**
 * Parse markdown-style links [text](url) in text and render as clickable <a> tags.
 * Only allows http/https URLs for security.
 */
function renderTextWithLinks(text: string): React.ReactNode {
  if (!text) return text;
  const linkRegex = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g;
  if (!linkRegex.test(text)) return text;
  // Reset regex state
  linkRegex.lastIndex = 0;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = linkRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    // Never show raw URL as link text, use domain name instead
    let linkText = match[1];
    if (/^https?:\/\//.test(linkText)) {
      try {
        linkText = new URL(linkText).hostname.replace('www.', '');
      } catch(e) {
        linkText = 'Link';
      }
    }
    parts.push(
      <a key={match.index} href={match[2]} target="_blank" rel="noopener noreferrer" className="flow-link">{linkText}</a>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return <>{parts}</>;
}

/**
 * AI Theme Player component
 */
export function AIThemePlayer({
  screen,
  theme,
  aspectRatio = { width: 1024, height: 748 },
  scale = 1,
  onImageResolved,
  onScreenNavigation,
}: AIThemePlayerProps) {
  const themeVars = useMemo(() => themeToCSSVars(theme), [theme]);

  const containerStyle: React.CSSProperties = {
    ...themeVars,
    aspectRatio: `${aspectRatio.width} / ${aspectRatio.height}`,
    transform: scale !== 1 ? `scale(${scale})` : undefined,
    transformOrigin: 'top left',
  };

  const decorationElements = useMemo(
    () => getDecorationsForScreen(theme.decorations, screen.screenType),
    [theme, screen.screenType]
  );

  // Check if this screen should have a hero (full-screen) background image
  const heroTypes = ['title', 'opening', 'closing', 'image-focus', 'quote-slide'];
  const isHeroScreen = heroTypes.includes(screen.screenType || '');

  // Find resolved image for hero background
  const heroImageBlock = useMemo(() => {
    if (!isHeroScreen) return null;
    return screen.blocks.find(
      (b) => b.type === 'image-placeholder' && (b as ImagePlaceholderBlock).resolvedImageUrl
    ) as ImagePlaceholderBlock | undefined;
  }, [screen.blocks, isHeroScreen]);

  const heroImageUrl = heroImageBlock?.resolvedImageUrl;

  const screenTypeClass = screen.screenType ? `screen-type-${screen.screenType}` : '';
  const heroClass = heroImageUrl ? 'has-hero-image' : '';

  return (
    <div className="theme-player" style={containerStyle}>
      <div className={`theme-player-slide ${screenTypeClass} ${heroClass}`}>
        {/* Hero background image */}
        {heroImageUrl && (
          <>
            <img src={heroImageUrl} alt="" className="theme-hero-bg" />
            <div className="theme-hero-overlay" />
          </>
        )}
        {/* Decoration elements */}
        {!heroImageUrl && decorationElements.map((element, i) => (
          <DecorationLayer key={`deco-${i}`} element={element} aspectRatio={aspectRatio} />
        ))}
        {/* Content blocks (skip hero image block since it's rendered as background) */}
        {screen.blocks.map((block) => {
          // Skip the hero image block - it's already rendered as background
          if (heroImageUrl && block.id === heroImageBlock?.id) return null;
          return (
            <ThemeBlock key={block.id} block={block} theme={theme} onImageResolved={onImageResolved} onScreenNavigation={onScreenNavigation} />
          );
        })}
      </div>
    </div>
  );
}

/**
 * Renders a decoration element as an absolutely positioned CSS layer.
 * Scales from the 1024x748 canvas to the container using percentages.
 */
function DecorationLayer({
  element,
  aspectRatio,
}: {
  element: DecorationElement;
  aspectRatio: { width: number; height: number };
}) {
  const { frame } = element;
  const pctLeft = (frame.x / aspectRatio.width) * 100;
  const pctTop = (frame.y / aspectRatio.height) * 100;
  const pctWidth = (frame.width / aspectRatio.width) * 100;
  const pctHeight = (frame.height / aspectRatio.height) * 100;

  const baseStyle: React.CSSProperties = {
    position: 'absolute',
    left: `${pctLeft}%`,
    top: `${pctTop}%`,
    width: `${pctWidth}%`,
    height: `${pctHeight}%`,
    pointerEvents: 'none',
    zIndex: 0,
  };

  if (element.shapeType === 'roundedRectangle') {
    baseStyle.borderRadius = '12px';
  } else if (element.shapeType === 'ellipse') {
    baseStyle.borderRadius = '50%';
  }

  switch (element.type) {
    case 'image':
      return (
        <img
          src={element.imageUrl}
          alt=""
          style={{
            ...baseStyle,
            objectFit: 'cover',
          }}
        />
      );

    case 'shape': {
      const { hex6, alpha } = parseDecorationColor(element.color || '#000000');
      return (
        <div
          style={{
            ...baseStyle,
            backgroundColor: hex6,
            opacity: alpha,
          }}
        />
      );
    }

    case 'gradient-shape': {
      if (!element.gradient) return null;
      return (
        <div
          style={{
            ...baseStyle,
            background: gradientToCSS(element.gradient),
          }}
        />
      );
    }

    default:
      return null;
  }
}

/**
 * Convert a GradientDef to a CSS gradient string
 */
function gradientToCSS(gradient: GradientDef): string {
  const stops = gradient.stops
    .map((s) => `${s.color} ${s.offset}%`)
    .join(', ');

  if (gradient.type === 'radial') {
    return `radial-gradient(circle, ${stops})`;
  }

  const angle = gradient.angle ?? 0;
  // CSS linear-gradient: 0deg = bottom-to-top, 180deg = top-to-bottom
  // Our convention: 0 = top-to-bottom, so add 180
  const cssAngle = (angle + 180) % 360;
  return `linear-gradient(${cssAngle}deg, ${stops})`;
}

/**
 * Theme block component - renders individual content blocks
 */
function ThemeBlock({ block, theme, onImageResolved, onScreenNavigation }: { block: ContentBlock; theme: StyleTheme; onImageResolved?: (blockId: string, imageUrl: string) => void; onScreenNavigation?: (screenIndex: number) => void }) {
  switch (block.type) {
    case 'title':
      return (
        <h1
          className={`theme-block theme-block-title theme-block-title-${block.level || 1}`}
          style={getTitleStyle(theme, block.level || 1)}
        >
          {block.text}
        </h1>
      );

    case 'subtitle':
      return (
        <h2 className="theme-block theme-block-subtitle" style={getSubtitleStyle(theme)}>
          {renderTextWithLinks(block.text)}
        </h2>
      );

    case 'body':
      return (
        <p
          className={`theme-block theme-block-body ${block.emphasis ? `theme-block-${block.emphasis}` : ''}`}
          style={getBodyStyle(theme, block.emphasis)}
        >
          {renderTextWithLinks(block.text)}
        </p>
      );

    case 'bullet-list':
      return (
        <ul className="theme-block theme-block-list theme-block-bullet-list">
          {block.items.map((item, i) => (
            <li key={i}>{renderTextWithLinks(item)}</li>
          ))}
        </ul>
      );

    case 'numbered-list':
      return (
        <ol
          className="theme-block theme-block-list theme-block-numbered-list"
          start={block.startNumber || 1}
        >
          {block.items.map((item, i) => (
            <li key={i}>{renderTextWithLinks(item)}</li>
          ))}
        </ol>
      );

    case 'quote':
      return (
        <blockquote className="theme-block theme-block-quote">
          <p>"{renderTextWithLinks(block.text)}"</p>
          {block.attribution && <cite>- {block.attribution}</cite>}
          {block.source && <span className="quote-source">{block.source}</span>}
        </blockquote>
      );

    case 'callout':
      return (
        <div
          className={`theme-block theme-block-callout theme-callout-${block.calloutType || 'info'}`}
          style={getCalloutStyle(theme, block.calloutType)}
        >
          {block.icon && <span className="callout-icon">{block.icon}</span>}
          <p>{renderTextWithLinks(block.text)}</p>
        </div>
      );

    case 'image-placeholder':
      return (
        <ImagePlaceholderRenderer
          block={block as ImagePlaceholderBlock}
          onImageResolved={onImageResolved}
        />
      );

    case 'video-placeholder':
      return (
        <div className="theme-block theme-block-video-placeholder">
          <svg viewBox="0 0 24 24" width="48" height="48">
            <path
              fill="currentColor"
              d="M8 5v14l11-7z"
            />
          </svg>
          <span>{block.description}</span>
        </div>
      );

    case 'code-block':
      return (
        <pre className="theme-block theme-block-code">
          <code className={block.language ? `language-${block.language}` : ''}>
            {block.code}
          </code>
          {block.filename && <span className="code-filename">{block.filename}</span>}
        </pre>
      );

    case 'caption':
      return (
        <p className="theme-block theme-block-caption">{block.text}</p>
      );

    case 'stat': {
      const statBlock = block as StatBlock;
      return (
        <div className="theme-block theme-block-stat">
          <div className={`stat-value${statBlock.value.length > 7 ? ' stat-value-long' : ''}`} style={{ color: theme.colors.accent?.[0] || theme.colors.primary }}>
            {statBlock.trend === 'up' && <span className="stat-trend stat-trend-up">↑</span>}
            {statBlock.trend === 'down' && <span className="stat-trend stat-trend-down">↓</span>}
            {statBlock.value}
          </div>
          <div className="stat-label">{statBlock.label}</div>
          {statBlock.context && <div className="stat-context">{statBlock.context}</div>}
        </div>
      );
    }

    case 'chart': {
      const chartBlock = block as ChartBlock;
      return (
        <ChartRenderer chart={chartBlock} theme={theme} />
      );
    }

    case 'chart-data':
      return (
        <div className="theme-block theme-block-chart-placeholder">
          <svg viewBox="0 0 24 24" width="48" height="48">
            <path
              fill="currentColor"
              d="M5 9.2h3V19H5zM10.6 5h2.8v14h-2.8zm5.6 8H19v6h-2.8z"
            />
          </svg>
          <span>{block.title || 'Chart'}</span>
        </div>
      );

    case 'table-data':
      return (
        <div className="theme-block theme-block-table">
          {block.title && <div className="table-title">{block.title}</div>}
          <table>
            <thead>
              <tr>
                {block.headers.map((header, i) => (
                  <th key={i}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) => (
                    <td key={j}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case 'speaker-notes':
      // Don't render speaker notes in the player
      return null;

    case 'toc-list': {
      const tocBlock = block as TOCListBlock;
      const entryCount = tocBlock.entries.length;
      const useGridLayout = entryCount >= 4; // Use grid for 4+ items
      const countClass = entryCount <= 6 ? `toc-count-${entryCount}` : 'toc-count-many';

      return (
        <div className={`theme-block theme-block-toc ${useGridLayout ? 'toc-grid' : 'toc-list'} ${countClass}`}>
          {tocBlock.entries.map((entry) => (
            <button
              key={entry.screenIndex}
              className="toc-entry"
              onClick={() => onScreenNavigation?.(entry.screenIndex)}
            >
              <span className="toc-entry-icon">
                {entry.imageUrl
                  ? <img src={entry.imageUrl} alt="" className="toc-entry-thumb" />
                  : entry.icon
                }
              </span>
              <span className="toc-entry-title">{entry.title}</span>
            </button>
          ))}
        </div>
      );
    }

    default:
      return null;
  }
}

/**
 * Image placeholder renderer with resolved image, shimmer, error + drag-drop fallback
 */
function ImagePlaceholderRenderer({
  block,
  onImageResolved,
}: {
  block: ImagePlaceholderBlock;
  onImageResolved?: (blockId: string, imageUrl: string) => void;
}) {
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);

    if (!onImageResolved) return;

    // Try URL from drag data
    const url = e.dataTransfer.getData('text/uri-list') || e.dataTransfer.getData('text/plain');
    if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
      const result = await aiService.uploadImageFromUrl(url, block.id);
      if (result.success && result.imageUrl) {
        onImageResolved(block.id, result.imageUrl);
      }
      return;
    }

    // Try file drop
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const result = await aiService.uploadImageFile(file, block.id);
      if (result.success && result.imageUrl) {
        onImageResolved(block.id, result.imageUrl);
      }
    }
  }, [block.id, onImageResolved]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  // Resolved: show actual image
  if (block.resolvedImageUrl) {
    return (
      <div className="theme-block theme-block-image-resolved">
        <img
          src={block.resolvedImageUrl}
          alt={block.altText || block.description}
          className="theme-resolved-image"
        />
      </div>
    );
  }

  // Error: show fallback with Google Images search
  if (block.imageStatus === 'error') {
    return (
      <div
        className={`theme-block theme-block-image-placeholder aspect-${block.aspectRatio?.replace(':', '-') || '16-9'} ${dragOver ? 'drag-over' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <div className="image-fallback-container">
          <svg viewBox="0 0 24 24" width="32" height="32">
            <path fill="currentColor" d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
          </svg>
          <span>Couldn't generate this image</span>
          <button
            className="image-search-btn"
            onClick={() => {
              const query = (block.suggestedKeywords || []).join(' ') || block.description;
              window.open(
                `https://www.google.com/search?q=${encodeURIComponent(query)}&tbm=isch`,
                'imageSearch',
                'width=800,height=600,scrollbars=yes'
              );
            }}
          >
            Search Google Images
          </button>
          <span className="image-drop-hint">Then drag & drop image here</span>
        </div>
      </div>
    );
  }

  // Generating: shimmer placeholder
  return (
    <div
      className={`theme-block theme-block-image-placeholder ${
        block.imageStatus === 'generating' ? 'shimmer' : ''
      } aspect-${block.aspectRatio?.replace(':', '-') || '16-9'} ${dragOver ? 'drag-over' : ''}`}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      {block.imageStatus === 'generating' ? (
        <>
          <div className="image-gen-spinner"></div>
          <span>Generating image...</span>
        </>
      ) : (
        <>
          <svg viewBox="0 0 24 24" width="48" height="48">
            <path
              fill="currentColor"
              d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"
            />
          </svg>
          <span>{block.description}</span>
        </>
      )}
    </div>
  );
}

/**
 * Chart renderer component
 */
function ChartRenderer({ chart, theme }: { chart: ChartBlock; theme: StyleTheme }) {
  const data = chart.data;
  if (!data || data.length === 0) {
    return <div className="theme-block theme-block-chart-empty">No data</div>;
  }

  const colorPalette = [
    theme.colors.primary,
    ...(theme.colors.accent || []),
    theme.colors.secondary,
    '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  ].filter(Boolean);

  if (chart.chartType === 'bar') {
    const maxValue = Math.max(...data.map(d => d.value));
    return (
      <div className="theme-block theme-block-chart theme-chart-bar">
        {chart.title && <div className="chart-title">{chart.title}</div>}
        <div className="chart-bars">
          {data.map((item, index) => {
            const width = (item.value / maxValue) * 100;
            const color = item.color || colorPalette[index % colorPalette.length];
            return (
              <div key={index} className="chart-bar-row">
                <span className="chart-bar-label">{item.label}</span>
                <div className="chart-bar-track">
                  <div
                    className="chart-bar-fill"
                    style={{ width: `${width}%`, backgroundColor: color }}
                  />
                </div>
                <span className="chart-bar-value">{item.value}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (chart.chartType === 'pie' || chart.chartType === 'donut') {
    const total = data.reduce((sum, d) => sum + d.value, 0);
    const size = 120;
    const strokeWidth = chart.chartType === 'donut' ? 20 : 60;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    let offset = 0;

    return (
      <div className="theme-block theme-block-chart theme-chart-pie">
        {chart.title && <div className="chart-title">{chart.title}</div>}
        <div className="chart-pie-container">
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            {data.map((item, index) => {
              const percentage = item.value / total;
              const dashLength = circumference * percentage;
              const dashOffset = circumference * offset;
              offset += percentage;
              const color = item.color || colorPalette[index % colorPalette.length];
              return (
                <circle
                  key={index}
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="none"
                  stroke={color}
                  strokeWidth={strokeWidth}
                  strokeDasharray={`${dashLength} ${circumference - dashLength}`}
                  strokeDashoffset={-dashOffset}
                  transform={`rotate(-90 ${size / 2} ${size / 2})`}
                />
              );
            })}
          </svg>
          <div className="chart-pie-legend">
            {data.map((item, index) => {
              const color = item.color || colorPalette[index % colorPalette.length];
              const percentage = ((item.value / total) * 100).toFixed(1);
              return (
                <div key={index} className="chart-legend-item">
                  <span className="chart-legend-dot" style={{ backgroundColor: color }} />
                  <span className="chart-legend-label">{item.label}</span>
                  <span className="chart-legend-value">{percentage}%</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return null;
}

/**
 * Style helpers
 */
function getTitleStyle(theme: StyleTheme, level: number): React.CSSProperties {
  const blockStyle = theme.blockStyles?.title || {};
  const fontSize = level === 1 ? 56 : level === 2 ? 44 : 32;
  return {
    fontFamily: blockStyle.fontName || theme.typography.titleFont,
    fontSize: blockStyle.fontSize || fontSize,
    color: blockStyle.textColor || theme.colors.textPrimary,
    fontWeight: blockStyle.fontWeight === 'light' ? 300 : blockStyle.fontWeight === 'bold' ? 700 : 600,
    textAlign: blockStyle.textAlignment || 'left',
    letterSpacing: blockStyle.letterSpacing,
  };
}

function getSubtitleStyle(theme: StyleTheme): React.CSSProperties {
  const blockStyle = theme.blockStyles?.subtitle || {};
  return {
    fontFamily: blockStyle.fontName || theme.typography.titleFont,
    fontSize: blockStyle.fontSize || 24,
    color: blockStyle.textColor || theme.colors.textSecondary,
    fontWeight: blockStyle.fontWeight === 'bold' ? 600 : 400,
    textAlign: blockStyle.textAlignment || 'left',
  };
}

function getBodyStyle(theme: StyleTheme, emphasis?: string): React.CSSProperties {
  const blockStyle = theme.blockStyles?.body || {};
  return {
    fontFamily: blockStyle.fontName || theme.typography.bodyFont,
    fontSize: blockStyle.fontSize || 20,
    color: blockStyle.textColor || theme.colors.textPrimary,
    fontWeight: emphasis === 'strong' ? 600 : 400,
    opacity: emphasis === 'subtle' ? 0.7 : 1,
    lineHeight: blockStyle.lineHeight || theme.typography.lineHeight,
    textAlign: blockStyle.textAlignment || 'left',
  };
}

function getCalloutStyle(
  theme: StyleTheme,
  type?: 'info' | 'warning' | 'success' | 'tip'
): React.CSSProperties {
  const blockStyle = theme.blockStyles?.callout || {};
  let bgColor = blockStyle.backgroundColor || theme.colors.accent[0];
  let textColor = blockStyle.textColor || theme.colors.textPrimary;

  // Adjust colors based on callout type
  if (type === 'warning' && theme.colors.warning) {
    bgColor = theme.colors.warning;
  } else if (type === 'success' && theme.colors.success) {
    bgColor = theme.colors.success;
  }

  return {
    backgroundColor: bgColor,
    color: textColor,
    borderRadius: blockStyle.borderRadius || 8,
    padding: typeof blockStyle.padding === 'number' ? blockStyle.padding : 16,
  };
}

/**
 * Slide thumbnail component for navigation
 */
export function AIThemeThumbnail({
  screen,
  theme,
  isSelected,
  index,
  onClick,
  capturedImage,
}: {
  screen: ScreenContent;
  theme: StyleTheme;
  isSelected: boolean;
  index: number;
  onClick: () => void;
  capturedImage?: string | null;
}) {
  const themeVars = useMemo(() => themeToCSSVars(theme), [theme]);

  return (
    <button
      className={`theme-thumbnail ${isSelected ? 'selected' : ''}`}
      onClick={onClick}
    >
      {capturedImage ? (
        <img
          src={capturedImage}
          alt={`Screen ${index + 1}`}
          className="theme-thumbnail-image"
        />
      ) : (
        <div className="theme-thumbnail-canvas" style={themeVars}>
          <div className="theme-thumbnail-content">
            {screen.blocks.slice(0, 3).map((block) => (
              <ThumbnailBlock key={block.id} block={block} />
            ))}
          </div>
        </div>
      )}
      <span className="theme-thumbnail-number">{index + 1}</span>
    </button>
  );
}

/**
 * Simplified block renderer for thumbnails
 */
function ThumbnailBlock({ block }: { block: ContentBlock }) {
  switch (block.type) {
    case 'title':
      return <div className="thumb-title">{truncateText(block.text, 30)}</div>;
    case 'subtitle':
      return <div className="thumb-subtitle">{truncateText(block.text, 40)}</div>;
    case 'body':
      return <div className="thumb-body">{truncateText(block.text, 50)}</div>;
    case 'bullet-list':
    case 'numbered-list':
      return (
        <div className="thumb-list">
          {block.items?.slice(0, 3).map((item, i) => (
            <div key={i} className="thumb-list-item">• {truncateText(item, 25)}</div>
          ))}
        </div>
      );
    default:
      return null;
  }
}

function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
}

function formatScreenType(type: string): string {
  return type
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export default AIThemePlayer;
